import { Injectable, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, ReplaySubject, catchError, finalize, map, of, shareReplay, switchMap, take, throwError } from 'rxjs';

import { DEFAULT_ORGANIZATION_BRANDING } from '../../../core/config/organization-branding.config';
import { API_ENDPOINTS } from '../../../core/constants/api-endpoints';
import { HttpErrorModel } from '../../../core/models/http-error.model';
import { PlatformRole } from '../../../core/models/role.model';
import { ApiService } from '../../../core/services/api.service';
import { ThemeMode } from '../../../core/models/organization-branding.model';
import { ThemeService } from '../../../core/services/theme.service';
import { LightPaletteCode, resolveLightPalette } from '../../../core/theme/light-palettes';
import { PreferredLanguage, PreferredTheme } from '../models/auth-user.model';
import { LoginRequest } from '../models/login-request.model';
import { LoginResult } from '../models/login-result.model';
import { NewPasswordChallenge } from '../models/new-password-challenge.model';
import { AuthSessionStorageService } from './auth-session-storage.service';
import { NewPasswordChallengeStore } from './new-password-challenge.store';
import { AuthSession } from '../models/auth-session.model';
import { AuthStateStore } from '../state/auth-state.store';
import { resolveId } from '../../../core/utils/id.util';

interface AuthRoleApiResponse {
  id?: string;
  name?: string;
}

interface LoginSessionApiResponse {
  access_token?: string;
  id_token?: string;
  token_type?: string;
  expires_in?: number;
}

interface LoginApiResponse {
  status?: string;
  access_token?: string;
  token?: string;
  user?: unknown;
  role?: AuthRoleApiResponse;
  permissions?: unknown;
  session?: LoginSessionApiResponse;
  challenge?: string;
  challenge_session?: string;
}

interface AuthMeApiResponse {
  user?: unknown;
  role?: AuthRoleApiResponse;
  permissions?: unknown;
}

interface RefreshApiResponse {
  status?: string;
  session?: LoginSessionApiResponse;
}

interface UserPreferencesPayload {
  preferred_language?: PreferredLanguage;
  preferred_theme?: PreferredTheme;
  theme_palette_code?: LightPaletteCode;
}

interface OrganizationThemeSettingsPayload {
  theme_palette_code?: LightPaletteCode;
  apply_to_all_users?: boolean;
}

export interface ForgotPasswordResponse {
  message?: string;
  reset_url?: string;
}

export interface ResetPasswordResponse {
  message?: string;
}

// Renew a refreshable access token this many milliseconds BEFORE it actually
// expires, so protected requests go out with a comfortably-valid token instead
// of racing the expiry (and eating a 401 round-trip). Kept small relative to the
// ~60 min Cognito access-token lifetime.
const ACCESS_TOKEN_EXPIRY_MARGIN_MS = 60_000;

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly apiService = inject(ApiService);
  private readonly translateService = inject(TranslateService);
  private readonly themeService = inject(ThemeService);
  private readonly authStateStore = inject(AuthStateStore);
  private readonly authSessionStorageService = inject(AuthSessionStorageService);
  private readonly newPasswordChallengeStore = inject(NewPasswordChallengeStore);
  private readonly authEndpoints = API_ENDPOINTS.auth;
  private readonly authInitializationCompleted$ = new ReplaySubject<void>(1);
  private authInitializationStarted = false;
  // Shared in-flight refresh so concurrent 401s trigger a single /auth/refresh.
  private refreshInFlight$: Observable<boolean> | null = null;

  readonly session = this.authStateStore.session;
  readonly isAuthenticated = this.authStateStore.isAuthenticated;
  readonly isLoading = this.authStateStore.isLoading;
  readonly isInitialized = this.authStateStore.isInitialized;
  readonly isAuthInitializing = computed(() => this.isLoading() && !this.isInitialized());
  readonly error = this.authStateStore.error;
  readonly currentUser = computed(() => this.session()?.user ?? null);

  constructor() {}

  waitForInitialization(): Observable<void> {
    if (this.isInitialized()) {
      return of(void 0);
    }

    this.initializeAuth();

    return this.authInitializationCompleted$.pipe(take(1));
  }

  hasStoredAccessToken(): boolean {
    const restoredSession = this.authSessionStorageService.loadSession();
    return Boolean(restoredSession?.accessToken?.trim());
  }

  login(request: LoginRequest): Observable<LoginResult> {
    this.authStateStore.setLoading(true);
    this.authStateStore.setError(null);

    // withCredentials is required so the browser stores the refresh token that the
    // backend returns as an HttpOnly cookie on a successful Cognito login.
    return this.apiService.post<LoginApiResponse, Pick<LoginRequest, 'email' | 'password'>>(this.authEndpoints.login, {
      email: request.email,
      password: request.password
    }, { withCredentials: true }).pipe(
      map((response) => this.mapApiLoginResponse(response, request)),
      switchMap((result) => {
        // Cognito first login: the account exists but requires a new password.
        // This is a normal outcome (not an error) — hand the challenge off to the
        // dedicated view and do NOT establish a session.
        if (result.status === 'NEW_PASSWORD_REQUIRED' && result.challenge) {
          this.newPasswordChallengeStore.set(result.challenge);
          this.authStateStore.setError(null);
          this.authStateStore.setLoading(false);
          return of(result);
        }

        if (!result.success || !result.session) {
          this.authStateStore.setError(result.error ?? 'Unable to authenticate with provided credentials.');
          this.authStateStore.setLoading(false);
          return of(result);
        }

        return this.establishSession(result.session);
      }),
      catchError((error: unknown) => {
        const authErrorMessage = this.resolveLoginErrorMessage(error);
        this.authStateStore.setError(authErrorMessage);
        this.authStateStore.setLoading(false);

        return of(this.errorResult(authErrorMessage));
      })
    );
  }

  /**
   * Completes the Cognito NEW_PASSWORD_REQUIRED challenge. On success the backend
   * returns the same AUTHENTICATED contract as a normal login and a session is
   * established. The refresh token is delivered via an HttpOnly cookie and is
   * never read from the body.
   */
  completeNewPassword(input: { email: string; newPassword: string; challengeSession: string }): Observable<LoginResult> {
    this.authStateStore.setLoading(true);
    this.authStateStore.setError(null);

    return this.apiService
      .post<LoginApiResponse, { email: string; new_password: string; challenge_session: string }>(
        this.authEndpoints.completeNewPassword,
        {
          email: input.email,
          new_password: input.newPassword,
          challenge_session: input.challengeSession
        },
        // Stores the refresh cookie just like login on a successful challenge.
        { withCredentials: true }
      )
      .pipe(
        map((response) => this.mapApiLoginResponse(response, { email: input.email, password: '', rememberMe: false })),
        switchMap((result) => {
          if (!result.success || !result.session) {
            this.authStateStore.setError(result.error ?? 'Unable to set your new password.');
            this.authStateStore.setLoading(false);
            return of(result);
          }

          return this.establishSession(result.session);
        }),
        catchError((error: unknown) => {
          const statusCode = this.resolveHttpStatus(error);
          const message = this.resolveCompleteNewPasswordErrorMessage(statusCode, error);
          this.authStateStore.setError(message);
          this.authStateStore.setLoading(false);

          return of({
            ...this.errorResult(message),
            requiresRelogin: statusCode === 401
          } satisfies LoginResult);
        })
      );
  }

  /**
   * Establishes a session from an authenticated login/challenge result: persists
   * the token, syncs /auth/me, applies preferences and updates the auth state.
   */
  private establishSession(loginSession: AuthSession): Observable<LoginResult> {
    this.authSessionStorageService.saveSession(loginSession);

    return this.fetchCurrentUserSession(loginSession).pipe(
      map((validatedSession) => {
        this.applyUserPreferences(validatedSession.user);
        this.authSessionStorageService.saveSession(validatedSession);
        this.authStateStore.setSession(validatedSession);

        return {
          status: 'AUTHENTICATED',
          success: true,
          session: validatedSession,
          challenge: null,
          error: null
        } satisfies LoginResult;
      }),
      catchError((error: unknown) => {
        const authMeErrorMessage = this.resolveAuthMeSyncErrorMessage(error);
        this.authSessionStorageService.clearSession();
        this.authStateStore.clearSession();
        this.authStateStore.setError(authMeErrorMessage);

        return of(this.errorResult(authMeErrorMessage));
      })
    );
  }

  private errorResult(message: string): LoginResult {
    return {
      status: 'ERROR',
      success: false,
      session: null,
      challenge: null,
      error: message
    };
  }

  /** Clears any pending NEW_PASSWORD_REQUIRED challenge (success or cancel). */
  clearNewPasswordChallenge(): void {
    this.newPasswordChallengeStore.clear();
  }

  getNewPasswordChallenge(): NewPasswordChallenge | null {
    return this.newPasswordChallengeStore.get();
  }

  hasPendingNewPasswordChallenge(): boolean {
    return this.newPasswordChallengeStore.hasChallenge();
  }

  forgotPassword(email: string): Observable<ForgotPasswordResponse> {
    return this.apiService.post<ForgotPasswordResponse, { email: string }>(this.authEndpoints.forgotPassword, {
      email
    });
  }

  resetPassword(token: string, newPassword: string): Observable<ResetPasswordResponse> {
    return this.apiService.post<ResetPasswordResponse, { token: string; new_password: string }>(this.authEndpoints.resetPassword, {
      token,
      new_password: newPassword
    });
  }

  /**
   * Completes a Cognito self-service password recovery with the emailed code. The
   * backend confirms the code and new password with Cognito; no tokens are issued
   * and no session is established here — the user signs in again afterwards.
   */
  confirmForgotPassword(input: { email: string; code: string; newPassword: string }): Observable<ResetPasswordResponse> {
    return this.apiService.post<ResetPasswordResponse, { email: string; code: string; new_password: string }>(
      this.authEndpoints.confirmForgotPassword,
      {
        email: input.email,
        code: input.code,
        new_password: input.newPassword
      }
    );
  }

  /**
   * Logs out the current session. Best-effort server-side revocation of the
   * refresh token (POST /auth/logout with the HttpOnly cookie), then ALWAYS clears
   * the local session — even if the network call fails. Navigation is handled by
   * the caller (shell / auth-error interceptor), preserving existing behavior.
   */
  logout(): void {
    this.apiService
      .post<unknown, Record<string, never>>(this.authEndpoints.logout, {}, { withCredentials: true })
      .pipe(catchError(() => of(void 0)))
      .subscribe();

    this.clearLocalSession();
  }

  /** Clears the local session (storage + in-memory state) without a network call. */
  clearLocalSession(): void {
    this.authSessionStorageService.clearSession();
    this.authStateStore.clearSession();
  }

  /**
   * Reports whether the current session can be renewed via the Cognito refresh
   * cookie. Only Cognito access tokens (token_use=access / cognito-idp issuer) are
   * refreshable; a legacy LOCAL JWT has no refresh cookie and must re-login.
   */
  canRefreshSession(): boolean {
    const token = this.session()?.accessToken ?? this.authSessionStorageService.loadAccessToken();

    if (!token?.trim()) {
      return false;
    }

    const payload = this.decodeJwtPayload(token);
    const issuer = this.resolveString(payload?.['iss']) ?? '';
    const tokenUse = this.resolveString(payload?.['token_use']) ?? '';

    return issuer.includes('cognito-idp') || tokenUse === 'access';
  }

  /**
   * Renews the access token using the refresh cookie (POST /auth/refresh). The
   * refresh token is never read from the response — it stays in the HttpOnly
   * cookie. Concurrent callers share a single in-flight request (single-flight);
   * the shared observable is cleared on completion (success or error).
   */
  refreshSession(): Observable<boolean> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.apiService
      .post<RefreshApiResponse, Record<string, never>>(this.authEndpoints.refresh, {}, { withCredentials: true })
      .pipe(
        map((response) => this.applyRefreshedTokens(response)),
        catchError(() => of(false)),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1)
      );

    return this.refreshInFlight$;
  }

  /**
   * Applies the tokens from a successful refresh to the current session (access
   * token + recomputed expiry). Returns false when there is no usable token or no
   * session to update.
   */
  private applyRefreshedTokens(response: RefreshApiResponse): boolean {
    const accessToken = this.resolveString(response.session?.access_token);

    if (!accessToken) {
      return false;
    }

    const currentSession = this.session() ?? this.authSessionStorageService.loadSession();

    if (!currentSession) {
      return false;
    }

    const updatedSession: AuthSession = {
      ...currentSession,
      accessToken,
      expiresAt: this.resolveTokenExpiration(accessToken)
    };

    this.authSessionStorageService.saveSession(updatedSession);
    this.authStateStore.setSession(updatedSession);

    return true;
  }

  getAccessToken(): string | null {
    if (!this.hasValidSession()) {
      return null;
    }

    return this.session()?.accessToken ?? null;
  }

  getRefreshToken(): string | null {
    return this.session()?.refreshToken ?? null;
  }

  hasValidSession(): boolean {
    const session = this.session();
    return !!session && session.expiresAt > Date.now();
  }

  isSessionExpired(): boolean {
    const session = this.session();
    return !!session && session.expiresAt <= Date.now();
  }

  /** Absolute expiry (epoch ms) of the stored access token, or null if none. */
  getAccessTokenExpiration(): number | null {
    const session = this.session() ?? this.authSessionStorageService.loadSession();
    return session?.expiresAt ?? null;
  }

  /**
   * Whether the stored access token is already expired or within the safety
   * margin of expiring. Reads the in-memory session first, falling back to
   * storage so the check also works during app initialization (before the state
   * store is hydrated). Drives the interceptor's proactive renewal.
   */
  isAccessTokenExpiringSoon(marginMs: number = ACCESS_TOKEN_EXPIRY_MARGIN_MS): boolean {
    const session = this.session() ?? this.authSessionStorageService.loadSession();

    if (!session || !session.accessToken?.trim()) {
      return false;
    }

    return session.expiresAt - marginMs <= Date.now();
  }

  canAttemptRefresh(): boolean {
    const refreshToken = this.getRefreshToken();
    return !!refreshToken && refreshToken.trim().length > 0;
  }

  refreshCurrentUser(): Observable<AuthSession['user']> {
    const sessionSource = this.session() ?? this.authSessionStorageService.loadSession();

    if (!sessionSource || !sessionSource.accessToken?.trim()) {
      return throwError(() => new Error('No access token available to refresh current user.'));
    }

    return this.fetchCurrentUserSession(sessionSource).pipe(
      map((validatedSession) => {
        this.applyUserPreferences(validatedSession.user);
        this.authSessionStorageService.saveSession(validatedSession);
        this.authStateStore.setSession(validatedSession);
        return validatedSession.user;
      })
    );
  }

  refreshCurrentUserSession(): Observable<boolean> {
    return this.refreshCurrentUser().pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  updatePreferredLanguage(language: PreferredLanguage): Observable<boolean> {
    return this.updateUserPreferences({ preferred_language: language });
  }

  updatePreferredTheme(theme: PreferredTheme): Observable<boolean> {
    return this.updateUserPreferences({ preferred_theme: theme });
  }

  updateThemePalette(paletteCode: string | null | undefined): Observable<boolean> {
    const nextPaletteCode = resolveLightPalette(paletteCode).code;
    return this.updateUserPreferences({ theme_palette_code: nextPaletteCode });
  }

  updateUserPreferences(preferences: UserPreferencesPayload): Observable<boolean> {
    const currentSession = this.session();

    if (!currentSession) {
      return of(false);
    }

    const nextPreferredLanguage = preferences.preferred_language ?? currentSession.user.preferred_language;
    const nextPreferredTheme = preferences.preferred_theme ?? currentSession.user.preferred_theme;
    const nextThemePaletteCode =
      preferences.theme_palette_code ?? currentSession.user.theme_palette_code ?? resolveLightPalette(undefined).code;

    const nextUser = {
      ...currentSession.user,
      preferred_language: nextPreferredLanguage,
      preferred_theme: nextPreferredTheme,
      theme_palette_code: nextThemePaletteCode,
      themePaletteCode: nextThemePaletteCode
    };

    const optimisticSession: AuthSession = {
      ...currentSession,
      user: nextUser
    };

    this.applyUserPreferences(nextUser);
    this.authSessionStorageService.saveSession(optimisticSession);
    this.authStateStore.setSession(optimisticSession);

    return this.persistUserPreferences({
      preferred_language: preferences.preferred_language,
      preferred_theme: preferences.preferred_theme,
      theme_palette_code: preferences.theme_palette_code
    }).pipe(
      map(() => true),
      catchError((error: unknown) => {
        this.applyUserPreferences(currentSession.user);
        this.authSessionStorageService.saveSession(currentSession);
        this.authStateStore.setSession(currentSession);
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates the organization-wide theme settings (self-service, ADMIN /
   * MASTER_ADMIN only — PATCH /organizations/me/theme). Mirrors
   * updateUserPreferences: the session user's organization_* fields are updated
   * optimistically (re-applying the effective palette) and rolled back on error.
   */
  updateOrganizationThemeSettings(settings: { applyToAllUsers?: boolean; themePaletteCode?: LightPaletteCode }): Observable<boolean> {
    const currentSession = this.session();

    if (!currentSession) {
      return of(false);
    }

    const payload: OrganizationThemeSettingsPayload = {
      ...(settings.themePaletteCode ? { theme_palette_code: settings.themePaletteCode } : {}),
      ...(settings.applyToAllUsers !== undefined ? { apply_to_all_users: settings.applyToAllUsers } : {})
    };

    if (payload.theme_palette_code === undefined && payload.apply_to_all_users === undefined) {
      return of(false);
    }

    const nextUser: AuthSession['user'] = {
      ...currentSession.user,
      organization_theme_palette_code:
        settings.themePaletteCode ?? currentSession.user.organization_theme_palette_code ?? resolveLightPalette(undefined).code,
      organization_apply_to_all_users: settings.applyToAllUsers ?? currentSession.user.organization_apply_to_all_users
    };

    const optimisticSession: AuthSession = {
      ...currentSession,
      user: nextUser
    };

    this.applyUserPreferences(nextUser);
    this.authSessionStorageService.saveSession(optimisticSession);
    this.authStateStore.setSession(optimisticSession);

    return this.apiService.patch<unknown, OrganizationThemeSettingsPayload>('/organizations/me/theme', payload).pipe(
      map(() => true),
      catchError((error: unknown) => {
        this.applyUserPreferences(currentSession.user);
        this.authSessionStorageService.saveSession(currentSession);
        this.authStateStore.setSession(currentSession);
        return throwError(() => error);
      })
    );
  }

  initializeAuth(): void {
    if (this.authInitializationStarted) {
      return;
    }

    this.authInitializationStarted = true;
    this.authStateStore.setLoading(true);
    this.authStateStore.setError(null);

    const tokenExists = this.hasStoredAccessToken();

    if (!tokenExists) {
      this.authStateStore.clearSession();
      this.completeAuthInitialization();
      return;
    }

    this.refreshCurrentUser().subscribe({
      next: () => {
        this.completeAuthInitialization();
      },
      error: (error: unknown) => {
        const statusCode = this.resolveHttpStatus(error);

        if (statusCode === 401) {
          this.authSessionStorageService.clearSession();
          this.authStateStore.clearSession();
          this.authStateStore.setError(this.resolveInitializationErrorMessage(error));
          this.completeAuthInitialization();
          return;
        }

        this.authStateStore.clearSession();
        this.authStateStore.setError(this.resolveInitializationErrorMessage(error));
        this.completeAuthInitialization();
      }
    });
  }

  private completeAuthInitialization(): void {
    if (!this.isInitialized()) {
      this.authStateStore.setInitialized(true);
    }

    this.authStateStore.setLoading(false);
    this.authInitializationCompleted$.next();
  }

  private fetchCurrentUserSession(session: AuthSession): Observable<AuthSession> {
    if (!session.accessToken?.trim()) {
      return throwError(() => new Error('No access token available for /auth/me request.'));
    }

    return this.apiService.get<AuthMeApiResponse>(this.authEndpoints.me).pipe(
      map((response) => {
        const user = this.mapAuthUser(response.user, response.role, session.user.email, session.accessToken, response.permissions);

        return {
          ...session,
          expiresAt: this.resolveTokenExpiration(session.accessToken),
          user
        };
      })
    );
  }

  private mapApiLoginResponse(response: LoginApiResponse, request: LoginRequest): LoginResult {
    // Primary discriminator is `status`, never the presence of an access token.
    if (response.status === 'NEW_PASSWORD_REQUIRED') {
      const challengeUser = this.toRecord(response.user);
      const email = this.resolveString(challengeUser?.['email']) ?? request.email;

      return {
        status: 'NEW_PASSWORD_REQUIRED',
        success: false,
        session: null,
        challenge: {
          email,
          challengeSession: this.resolveString(response.challenge_session) ?? '',
          user: {
            id: resolveId(challengeUser?.['id']),
            email,
            firstName: this.resolveString(challengeUser?.['first_name']) ?? this.resolveString(challengeUser?.['firstName']) ?? '',
            lastName: this.resolveString(challengeUser?.['last_name']) ?? this.resolveString(challengeUser?.['lastName']) ?? ''
          }
        },
        error: null
      };
    }

    // AUTHENTICATED (Cognito wraps the token in `session`) or the legacy LOCAL
    // contract (top-level `access_token`).
    const accessToken = this.resolveAccessToken(response);

    if (!accessToken) {
      return this.errorResult('Authentication response did not include an access token.');
    }

    const session: AuthSession = {
      accessToken,
      refreshToken: undefined,
      expiresAt: this.resolveTokenExpiration(accessToken),
      rememberMe: request.rememberMe,
      user: this.mapAuthUser(response.user, response.role, request.email, accessToken, response.permissions)
    };

    return {
      status: 'AUTHENTICATED',
      success: true,
      session,
      challenge: null,
      error: null
    };
  }

  private resolveCompleteNewPasswordErrorMessage(statusCode: number | null, error: unknown): string {
    switch (statusCode) {
      case 401:
        return 'Invalid or expired password setup session. Please sign in again.';
      case 400: {
        const httpError = this.toHttpErrorModel(error);
        return httpError?.message?.trim().length
          ? httpError.message
          : 'Your new password does not meet the requirements.';
      }
      case 429:
        return 'Too many attempts. Please try again later.';
      case 502:
      case 503:
        return 'Authentication service is temporarily unavailable.';
      default:
        return 'Unable to set your new password. Please try again.';
    }
  }

  private resolveRoleCodeFromSources(
    user: Record<string, unknown> | null,
    role: AuthRoleApiResponse | undefined,
    roleRecordFromUser: Record<string, unknown> | null
  ): string | undefined {
    return (
      this.resolveString(roleRecordFromUser?.['code']) ??
      this.resolveString(role?.name) ??
      this.resolveString(user?.['role_code']) ??
      this.resolveString(user?.['roleCode']) ??
      undefined
    );
  }

  private resolveRoleCodeFromSourcesCamelCase(
    user: Record<string, unknown> | null,
    role: AuthRoleApiResponse | undefined,
    roleRecordFromUser: Record<string, unknown> | null
  ): string | undefined {
    return (
      this.resolveString(roleRecordFromUser?.['code']) ??
      this.resolveString(role?.name) ??
      this.resolveString(user?.['roleCode']) ??
      this.resolveString(user?.['role_code']) ??
      undefined
    );
  }

  private resolveAuthMeSyncErrorMessage(error: unknown): string {
    const statusCode = this.resolveHttpStatus(error);

    if (statusCode === 401 || statusCode === 403) {
      return 'Unable to load your user profile. Please sign in again.';
    }

    const httpError = this.toHttpErrorModel(error);

    if (httpError?.message) {
      return httpError.message;
    }

    return 'Unable to load your user profile from /auth/me.';
  }

  private mapAuthUser(
    rawUser: unknown,
    role: AuthRoleApiResponse | undefined,
    fallbackEmail: string,
    accessToken?: string,
    responsePermissions?: unknown
  ): AuthSession['user'] {
    const user = this.toRecord(rawUser);
    const roleRecordFromUser = this.toRecord(user?.['role']);
    const email = this.resolveString(user?.['email']) ?? fallbackEmail;
    const firstName = this.resolveString(user?.['firstName']) ?? this.resolveString(user?.['first_name']);
    const lastName = this.resolveString(user?.['lastName']) ?? this.resolveString(user?.['last_name']);
    const fullName = this.resolveString(user?.['fullName']) ?? this.resolveString(user?.['full_name']) ?? this.resolveString(user?.['name']);
    const resolvedFullName = fullName ?? [firstName, lastName].filter((value): value is string => Boolean(value)).join(' ');
    const resolvedRoles = this.resolveRoles(user, role, accessToken);
    const roleName = this.resolveRoleNameFromSources(user, role);
    const roleId = this.resolveRoleId(user, role, accessToken);
    const userId =
      resolveId(user?.['user_id']) ??
      resolveId(user?.['userId']) ??
      resolveId(user?.['id']) ??
      resolveId(user?.['uuid']) ??
      null;
    // No fallback. This used to default to organization 1 — the system
    // organization's old autoincremented id — whenever neither the payload nor
    // the token carried one, which silently scoped the session to the wrong
    // tenant instead of failing. There is no id to guess at now, and null is
    // the honest answer: the caller can tell the session is incomplete.
    const organizationId =
      this.resolveOrganizationId(user) ??
      this.resolveOrganizationIdFromToken(accessToken);
    const organizationName = this.resolveOrganizationName(user);
    const roleCode = this.resolveRoleCodeFromSources(user, role, roleRecordFromUser);
    const roleCodeCamelCase = this.resolveRoleCodeFromSourcesCamelCase(user, role, roleRecordFromUser);

    return {
      user_id: userId,
      id: this.resolveString(user?.['id']) ?? this.resolveString(user?.['uuid']) ?? email,
      email,
      fullName: resolvedFullName || email,
      role_id: roleId,
      role_name: roleName,
      role_code: roleCode,
      roleCode: roleCodeCamelCase,
      role:
        roleRecordFromUser ??
        this.toRecord(role) ??
        this.resolveString(user?.['role']) ??
        undefined,
      roles: resolvedRoles,
      permissions: this.resolvePermissionList(responsePermissions ?? user?.['permissions']),
      authorities: this.resolvePermissionList(user?.['authorities']),
      scopes: this.resolvePermissionList(user?.['scopes'] ?? user?.['scope']),
      role_permissions: this.resolvePermissionList(user?.['role_permissions']),
      schoolId: this.resolveSchoolId(user, accessToken),
      schoolName: this.resolveString(user?.['school_name']) ?? null,
      organization_id: organizationId,
      organizationId,
      organization_name: organizationName ?? undefined,
      organization_logo: this.resolveOrganizationLogo(user),
      preferred_language: this.resolvePreferredLanguage(user),
      preferred_theme: this.resolvePreferredTheme(user),
      theme_palette_code: this.resolveThemePaletteCode(user),
      themePaletteCode: this.resolveThemePaletteCode(user),
      organization_theme_palette_code: this.resolveOrganizationThemePaletteCode(user),
      organization_apply_to_all_users: this.resolveOrganizationApplyToAllUsers(user)
    };
  }

  private persistUserPreferences(preferences: UserPreferencesPayload): Observable<unknown> {
    const payload: UserPreferencesPayload = {
      ...(preferences.preferred_language ? { preferred_language: preferences.preferred_language } : {}),
      ...(preferences.preferred_theme ? { preferred_theme: preferences.preferred_theme } : {}),
      ...(preferences.theme_palette_code ? { theme_palette_code: preferences.theme_palette_code } : {})
    };

    if (!payload.preferred_language && !payload.preferred_theme && !payload.theme_palette_code) {
      return of(void 0);
    }

    const preferenceEndpoints = [
      '/users/me/preferences',
      this.authEndpoints.preferences,
      this.authEndpoints.mePreferences,
      this.authEndpoints.me
    ];

    return this.patchPreferencesWithFallback(preferenceEndpoints, payload);
  }

  private patchPreferencesWithFallback(endpoints: ReadonlyArray<string>, payload: UserPreferencesPayload): Observable<unknown> {
    const [currentEndpoint, ...remainingEndpoints] = endpoints;

    if (!currentEndpoint) {
      return throwError(() => new Error('Unable to persist user preferences.'));
    }

    return this.apiService.patch<unknown, UserPreferencesPayload>(currentEndpoint, payload).pipe(
      catchError((error: unknown) => {
        const statusCode = this.resolveHttpStatus(error);

        if ((statusCode === 404 || statusCode === 405) && remainingEndpoints.length > 0) {
          return this.patchPreferencesWithFallback(remainingEndpoints, payload);
        }

        return throwError(() => error);
      })
    );
  }

  private applyUserPreferences(user: AuthSession['user']): void {
    const preferredLanguage = user.preferred_language === 'es' ? 'es' : 'en';
    const preferredThemeMode: ThemeMode = user.preferred_theme === 'dark' ? 'dark' : 'light';

    this.translateService.use(preferredLanguage);
    this.themeService.setLightPalette(this.resolveEffectiveThemePaletteCode(user));
    this.themeService.setMode(DEFAULT_ORGANIZATION_BRANDING.theme, preferredThemeMode);
  }

  /**
   * Resolves the palette that must actually be rendered: when the organization
   * enforces its own palette (organization_apply_to_all_users = true) every user
   * of the organization uses the organization palette, EXCEPT master admins who
   * always keep their personal preference.
   */
  private resolveEffectiveThemePaletteCode(user: AuthSession['user']): LightPaletteCode {
    const personalPaletteCode = resolveLightPalette(user.theme_palette_code ?? user.themePaletteCode).code;

    if (user.organization_apply_to_all_users !== true || this.isMasterAdminSessionUser(user)) {
      return personalPaletteCode;
    }

    return resolveLightPalette(user.organization_theme_palette_code ?? personalPaletteCode).code;
  }

  private isMasterAdminSessionUser(user: AuthSession['user']): boolean {
    const roleCode = (user.role_code ?? user.roleCode ?? '').trim().toUpperCase();
    return roleCode === 'MASTER_ADMIN' || roleCode === 'SUPER_ADMIN' || user.roles.includes('master_admin');
  }

  private resolveAccessToken(response: LoginApiResponse): string | null {
    // Cognito returns the token inside `session`; the legacy LOCAL flow returns it
    // at the top level.
    const sessionToken = this.resolveString(response.session?.access_token);

    if (sessionToken) {
      return sessionToken;
    }

    const preferredToken = this.resolveString(response.access_token);

    if (preferredToken) {
      return preferredToken;
    }

    return this.resolveString(response.token) ?? null;
  }

  private resolveTokenExpiration(token: string): number {
    const tokenSegments = token.split('.');

    if (tokenSegments.length !== 3) {
      return Date.now() + 1000 * 60 * 60 * 8;
    }

    try {
      const base64Payload = tokenSegments[1].replace(/-/g, '+').replace(/_/g, '/');
      const normalizedPayload = base64Payload.padEnd(Math.ceil(base64Payload.length / 4) * 4, '=');
      const payload = JSON.parse(atob(normalizedPayload)) as { exp?: unknown };
      const expInSeconds = typeof payload.exp === 'number' ? payload.exp : Number(payload.exp);

      if (!Number.isFinite(expInSeconds) || expInSeconds <= 0) {
        return Date.now() + 1000 * 60 * 60 * 8;
      }

      return expInSeconds * 1000;
    } catch {
      return Date.now() + 1000 * 60 * 60 * 8;
    }
  }

  private resolveRoles(
    user: Record<string, unknown> | null,
    role: AuthRoleApiResponse | undefined,
    accessToken?: string
  ): ReadonlyArray<PlatformRole> {
    const rawRoles = Array.isArray(user?.['roles']) ? user?.['roles'] : [];

    const normalizedRoles = rawRoles.flatMap((rawRole) => {
      const roleName = this.resolveRoleName(rawRole);
      return roleName ? [roleName] : [];
    });

    if (normalizedRoles.length > 0) {
      return Array.from(new Set(normalizedRoles));
    }

    const fallbackRole =
      this.normalizeRole(this.resolveString(role?.name)) ??
      this.normalizeRole(this.resolveString(user?.['role'])) ??
      this.normalizeRole(this.resolveString(user?.['roleName'])) ??
      this.resolveRoleFromToken(accessToken) ??
      'teacher';

    return [fallbackRole];
  }

  private resolveRoleName(rawRole: unknown): PlatformRole | null {
    if (typeof rawRole === 'string') {
      return this.normalizeRole(rawRole);
    }

    const roleRecord = this.toRecord(rawRole);
    return this.normalizeRole(this.resolveString(roleRecord?.['name']));
  }

  private normalizeRole(role: string | undefined): PlatformRole | null {
    if (!role) {
      return null;
    }

    const normalizedRole = role.trim().toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');

    if (normalizedRole === 'master-admin' || normalizedRole === 'masteradmin' || normalizedRole === 'super-admin' || normalizedRole === 'superadmin') {
      return 'master_admin';
    }

    if (normalizedRole === 'school-admin' || normalizedRole === 'schooladmin' || normalizedRole === 'admin') {
      return 'admin';
    }

    if (normalizedRole === 'sales' || normalizedRole === 'sales-user' || normalizedRole === 'salesuser') {
      return 'sales';
    }

    if (normalizedRole === 'teacher') {
      return 'teacher';
    }

    if (normalizedRole === 'student') {
      return 'student';
    }

    if (normalizedRole === 'parent') {
      return 'parent';
    }

    return null;
  }

  private resolveRoleFromToken(accessToken?: string): PlatformRole | null {
    if (!accessToken) {
      return null;
    }

    const tokenPayload = this.decodeJwtPayload(accessToken);

    if (!tokenPayload) {
      return null;
    }

    const tokenRoles = Array.isArray(tokenPayload['roles']) ? tokenPayload['roles'] : [];

    for (const rawRole of tokenRoles) {
      const resolvedTokenRole = this.resolveRoleName(rawRole);

      if (resolvedTokenRole) {
        return resolvedTokenRole;
      }
    }

    return (
      this.normalizeRole(this.resolveString(tokenPayload['role'])) ??
      this.normalizeRole(this.resolveString(tokenPayload['role_name'])) ??
      this.normalizeRole(this.resolveString(tokenPayload['roleName'])) ??
      null
    );
  }

  private resolveSchoolId(user: Record<string, unknown> | null, accessToken?: string): string | null {
    const userSchoolId =
      resolveId(user?.['schoolId']) ??
      resolveId(user?.['school_id']) ??
      resolveId(user?.['schoolID']) ??
      resolveId(user?.['school']);

    if (userSchoolId !== null) {
      return userSchoolId;
    }

    if (!accessToken) {
      return null;
    }

    const tokenPayload = this.decodeJwtPayload(accessToken);

    return (
      resolveId(tokenPayload?.['school_id']) ??
      resolveId(tokenPayload?.['schoolId']) ??
      resolveId(tokenPayload?.['schoolID']) ??
      null
    );
  }

  private resolveRoleId(
    user: Record<string, unknown> | null,
    role: AuthRoleApiResponse | undefined,
    accessToken?: string
  ): string | null {
    const userRoleId =
      resolveId(user?.['role_id']) ??
      resolveId(user?.['roleId']) ??
      resolveId(user?.['roleID']) ??
      resolveId(user?.['role_id_fk']);

    if (userRoleId !== null) {
      return userRoleId;
    }

    const roleResponseId = resolveId(role?.id);

    if (roleResponseId !== null) {
      return roleResponseId;
    }

    if (!accessToken) {
      return null;
    }

    const tokenPayload = this.decodeJwtPayload(accessToken);

    return (
      resolveId(tokenPayload?.['role_id']) ??
      resolveId(tokenPayload?.['roleId']) ??
      resolveId(tokenPayload?.['roleID']) ??
      null
    );
  }

  private resolveRoleNameFromSources(
    user: Record<string, unknown> | null,
    role: AuthRoleApiResponse | undefined
  ): string {
    return (
      this.resolveString(role?.name) ??
      this.resolveString(user?.['role_name']) ??
      this.resolveString(user?.['roleName']) ??
      this.resolveString(user?.['role']) ??
      ''
    );
  }

  private resolveOrganizationId(user: Record<string, unknown> | null): string | null {
    return (
      resolveId(user?.['organization_id']) ??
      resolveId(user?.['organizationId']) ??
      resolveId(user?.['org_id']) ??
      resolveId(user?.['orgId']) ??
      null
    );
  }

  private resolveOrganizationIdFromToken(accessToken?: string): string | null {
    if (!accessToken) {
      return null;
    }

    const tokenPayload = this.decodeJwtPayload(accessToken);

    return (
      resolveId(tokenPayload?.['organization_id']) ??
      resolveId(tokenPayload?.['organizationId']) ??
      resolveId(tokenPayload?.['org_id']) ??
      resolveId(tokenPayload?.['orgId']) ??
      null
    );
  }

  private resolveOrganizationName(user: Record<string, unknown> | null): string | null {
    const organizationRecord = this.toRecord(user?.['organization']);

    return (
      this.resolveString(organizationRecord?.['name']) ??
      this.resolveString(organizationRecord?.['organization_name']) ??
      this.resolveString(organizationRecord?.['display_name']) ??
      this.resolveString(organizationRecord?.['title']) ??
      this.resolveString(user?.['organization']) ??
      this.resolveString(user?.['organization_name']) ??
      this.resolveString(user?.['organizationName']) ??
      this.resolveString(user?.['org_name']) ??
      this.resolveString(user?.['orgName']) ??
      null
    );
  }

  // The organization's logo KEY from /auth/me. A blank key means "no logo": the
  // client draws its own fallback rather than requesting an object that is not
  // there.
  private resolveOrganizationLogo(user: Record<string, unknown> | null): string | null {
    const organizationRecord = this.toRecord(user?.['organization']);

    return (
      this.resolveString(user?.['organization_logo']) ??
      this.resolveString(user?.['organizationLogo']) ??
      this.resolveString(organizationRecord?.['logo']) ??
      null
    );
  }

  private resolvePreferredLanguage(user: Record<string, unknown> | null): PreferredLanguage {
    const rawLanguage =
      this.resolveString(user?.['preferred_language']) ??
      this.resolveString(user?.['preferredLanguage']) ??
      this.resolveString(user?.['language']) ??
      this.resolveString(user?.['lang']);

    return rawLanguage?.trim().toLowerCase() === 'es' ? 'es' : 'en';
  }

  private resolvePreferredTheme(user: Record<string, unknown> | null): PreferredTheme {
    const rawTheme =
      this.resolveString(user?.['preferred_theme']) ??
      this.resolveString(user?.['preferredTheme']) ??
      this.resolveString(user?.['theme']) ??
      this.resolveString(user?.['mode']);

    return rawTheme?.trim().toLowerCase() === 'dark' ? 'dark' : 'light';
  }

  private resolveThemePaletteCode(user: Record<string, unknown> | null): LightPaletteCode {
    const rawPaletteCode =
      this.resolveString(user?.['theme_palette_code']) ??
      this.resolveString(user?.['themePaletteCode']) ??
      this.resolveString(user?.['theme_palette']) ??
      this.resolveString(user?.['themePalette']) ??
      this.resolveString(user?.['palette_code']) ??
      this.resolveString(user?.['paletteCode']);

    return resolveLightPalette(rawPaletteCode).code;
  }

  private resolveOrganizationThemePaletteCode(user: Record<string, unknown> | null): LightPaletteCode {
    const rawPaletteCode =
      this.resolveString(user?.['organization_theme_palette_code']) ??
      this.resolveString(user?.['organizationThemePaletteCode']);

    return resolveLightPalette(rawPaletteCode).code;
  }

  private resolveOrganizationApplyToAllUsers(user: Record<string, unknown> | null): boolean | undefined {
    const rawValue = user?.['organization_apply_to_all_users'] ?? user?.['organizationApplyToAllUsers'];
    return typeof rawValue === 'boolean' ? rawValue : undefined;
  }

  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    const tokenSegments = token.split('.');

    if (tokenSegments.length !== 3) {
      return null;
    }

    try {
      const base64Payload = tokenSegments[1].replace(/-/g, '+').replace(/_/g, '/');
      const normalizedPayload = base64Payload.padEnd(Math.ceil(base64Payload.length / 4) * 4, '=');
      const payload = JSON.parse(atob(normalizedPayload));
      return this.toRecord(payload);
    } catch {
      return null;
    }
  }

  private resolveLoginErrorMessage(error: unknown): string {
    const fallbackMessage = 'Unable to authenticate with provided credentials.';
    const httpError = this.toHttpErrorModel(error);

    if (!httpError) {
      return fallbackMessage;
    }

    if (httpError.status === 401 || httpError.status === 403) {
      return 'Invalid email or password.';
    }

    return httpError.message?.trim().length ? httpError.message : fallbackMessage;
  }

  private toHttpErrorModel(error: unknown): HttpErrorModel | null {
    if (!this.toRecord(error)) {
      return null;
    }

    const status = Number((error as Record<string, unknown>)['status']);
    const code = this.resolveString((error as Record<string, unknown>)['code']);
    const message = this.resolveString((error as Record<string, unknown>)['message']);

    if (!Number.isFinite(status) || !code || !message) {
      return null;
    }

    return {
      status,
      code,
      message,
      details: (error as Record<string, unknown>)['details'],
      traceId: this.resolveString((error as Record<string, unknown>)['traceId']),
      path: this.resolveString((error as Record<string, unknown>)['path']),
      validationErrors: undefined
    };
  }

  private resolveHttpStatus(error: unknown): number | null {
    const errorRecord = this.toRecord(error);

    if (!errorRecord) {
      return null;
    }

    const statusCode = Number(errorRecord['status']);
    return Number.isFinite(statusCode) ? statusCode : null;
  }

  private resolveInitializationErrorMessage(error: unknown): string {
    const statusCode = this.resolveHttpStatus(error);

    if (statusCode === 401) {
      return 'Your session expired. Please sign in again.';
    }

    const httpError = this.toHttpErrorModel(error);
    if (httpError?.message) {
      return httpError.message;
    }

    return 'Unable to restore your session. Please sign in again.';
  }

  private toRecord(value: unknown): Record<string, unknown> | null {
    return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
  }

  private resolveString(value: unknown): string | undefined {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'bigint') {
      return String(value);
    }

    return undefined;
  }

  private resolveNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      const parsedNumber = Number(value);

      if (Number.isFinite(parsedNumber)) {
        return parsedNumber;
      }
    }

    return null;
  }

  private resolvePermissionList(source: unknown): ReadonlyArray<string> {
    if (!source) {
      return [];
    }

    if (typeof source === 'string') {
      const normalizedCode = this.normalizePermissionCode(source);
      return normalizedCode ? [normalizedCode] : [];
    }

    if (Array.isArray(source)) {
      const permissions = source.flatMap((entry) => this.resolvePermissionList(entry));
      return Array.from(new Set(permissions));
    }

    const sourceRecord = this.toRecord(source);

    if (!sourceRecord) {
      return [];
    }

    const directCode =
      this.normalizePermissionCode(sourceRecord['permission_code']) ??
      this.normalizePermissionCode(sourceRecord['code']) ??
      this.normalizePermissionCode(sourceRecord['name']) ??
      this.normalizePermissionCode(sourceRecord['permission']) ??
      this.normalizePermissionCode(sourceRecord['authority']);

    const nestedPermissions = [
      ...this.resolvePermissionList(sourceRecord['permissions']),
      ...this.resolvePermissionList(sourceRecord['authorities'])
    ];

    const collectedPermissions = directCode ? [directCode, ...nestedPermissions] : nestedPermissions;

    return Array.from(new Set(collectedPermissions));
  }

  private normalizePermissionCode(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const normalizedValue = value.trim().toLowerCase().replace(/[.\s-]+/g, '_');
    return normalizedValue.length > 0 ? normalizedValue : null;
  }
}
